import { SELECTION_RANGE_END, SELECTION_RANGE_START } from '@aline/common';
import type { ExtractedParagraph } from '@aline/common';

import type { DocOrSelectionMentionElement } from './utils';

/**
 * Counts the words in the user's selection for the chip's "(N words)" label.
 *
 * v1 (`'selection'`) splits partially-selected paragraphs into part rows and
 * marks only the middle row `selectionStatus: 'SELECTED'`, so summing words
 * across all SELECTED rows yields the selected text.
 *
 * v2 (`'selection-v2'`) keeps each affected paragraph as one row and marks
 * in-selection runs with `seg.selected: true`. The before/after context of a
 * partially-selected paragraph lives in the same row without that flag, so
 * we count only `selected: true` segments — and skip the `⟪`/`⟫` marker-
 * glyph segments so they don't fuse with adjacent words.
 *
 * Returns `null` for non-selection mentions so the caller can omit the
 * "(N words)" suffix entirely.
 */
export function countSelectedWords(element: DocOrSelectionMentionElement): number | null {
  switch (element.mentionTypeName) {
    case 'document':
      return null;
    case 'selection':
      return countSelectionStatusWords(element.extractedContext.paragraphs);
    case 'selection-v2': {
      const selectedSegmentCount = element.extractedContext.paragraphs.reduce(
        (sum, para) => sum + para.segments.filter(seg => seg.selected === true).length,
        0
      );
      if (!selectedSegmentCount)
        return countSelectionStatusWords(element.extractedContext.paragraphs);

      return countSelectedSegmentWords(element.extractedContext.paragraphs);
    }
    case 'selection-v3':
      return countWordsInSelectionHtml(element.html);
  }
}

const SELECTION_START_MARKER = '<selection-start/>';
const SELECTION_END_MARKER = '<selection-end/>';

/**
 * v3 (`'selection-v3'`) carries pre-serialized HTML. The user's selection is
 * the span between the `<selection-start/>` and `<selection-end/>` markers, so
 * we slice that span, strip the HTML tags (which removes the `<p final="...">`
 * attributes too, since those live inside the tag), and count the remaining
 * words. A collapsed cursor has no end marker, so it counts as zero.
 */
function countWordsInSelectionHtml(html: string): number {
  const start = html.indexOf(SELECTION_START_MARKER);
  const end = html.indexOf(SELECTION_END_MARKER);
  if (start === -1 || end === -1 || end < start) return 0;

  const span = html.slice(start + SELECTION_START_MARKER.length, end);
  const text = span.replace(/<[^>]*>/g, ' ');
  return countWords(text);
}

function countSelectionStatusWords(paragraphs: ExtractedParagraph[]) {
  return paragraphs
    .filter(p => p.selectionStatus === 'SELECTED')
    .reduce((sum, para) => {
      const text = para.segments.map(seg => seg.text).join('');
      return sum + countWords(text);
    }, 0);
}

function countSelectedSegmentWords(paragraphs: ExtractedParagraph[]) {
  /**
   * Reduce per paragraph so the trailing word of one paragraph doesn't fuse
   * with the leading word of the next when we split on whitespace.
   */
  return paragraphs.reduce((sum, para) => {
    const text = para.segments
      .filter(
        seg =>
          seg.selected === true &&
          seg.text !== SELECTION_RANGE_START &&
          seg.text !== SELECTION_RANGE_END
      )
      .map(seg => seg.text)
      .join('');
    return sum + countWords(text);
  }, 0);
}

function countWords(text: string): number {
  return text.split(/\s+/).filter(w => w).length;
}
